import './style.scss'
import ChartTpl from './templates/chart.hamlc'
import _ from 'underscore'
import URI from 'urijs'
import Backbone from '@polleverywhere/backbone'
import { getScale, proxyActivity, colorToRgba, tokenColor } from '@polleverywhere/viz-utils'
import User from '@polleverywhere/core/lib/models/user'
import { proxiedImageUrl } from '@polleverywhere/core/lib/util'
import { Dimensions, Settings } from '@polleverywhere/viz-models'
import { ResultsContainer } from './results_container'
import { ActivityNotSupportedError } from './ActivityNotSupportedError'

// Header
import { VotingInstructions } from '@polleverywhere/viz-voting-instructions'
import { ChartTitle } from '@polleverywhere/viz-chart-title'

// These top 2 are dependencies for imagesloaded
import imagesLoaded from 'imagesloaded'

// provide jQuery argument
imagesLoaded.makeJQueryPlugin(Backbone.$)

const NON_CHART_VIEW_STATES = [
  'title'
]

const SUPPORTED_ACTIVITIES = [
  'competition',
  'multiple_choice_poll',
  'ranking_poll',
  'clickable_image',
  'discourse',
  'free_text_poll',
  'survey',
  'join_instructions'
]

function determineToReRender () {
  const previousDisplayState = this.model.previousAttributes().display_state
  const currentDisplayState = this.model.get('display_state')
  const noChange = previousDisplayState === currentDisplayState

  if (noChange) { return false }

  const isPreviousChartView = previousDisplayState && !NON_CHART_VIEW_STATES.includes(previousDisplayState)
  const isCurrentChartView = !NON_CHART_VIEW_STATES.includes(this.model.get('display_state'))

  return isPreviousChartView !== isCurrentChartView
}

function renderChartOrTitle () {
  return new Promise((resolve, reject) => {
    switch (this.model.type()) {
      case 'multiple_choice_poll':
        if (NON_CHART_VIEW_STATES.includes(this.model.get('display_state'))) {
          return resolve(renderTitle.call(this))
        } else {
          return resolve(renderChart.call(this))
        }
      default:
        return resolve(renderChart.call(this))
    }
  })
}

function renderChart () {
  this.$el.toggleClass('chart--full-title', false)

  // TODO: Provide a better view state management mechanism
  // Deproxying attributes from the title view
  this.flashOptions.deproxy('voting_instructions_background_color', { silent: true })
  this.flashOptions.deproxy('voting_instructions_text_color', { silent: true })
  this.model.deproxy('sms_enabled', { silent: true })
  this.model.deproxy('web_enabled', { silent: true })
  this.model.deproxy('current', { silent: true })

  return new Promise((resolve, reject) => {
    renderChartFrame.call(this)
      .then(() => this.scaleBody())
      .then(() => this.dimensions.update())
      .then(() => renderVisualization.call(this))
      .then(() => this.resizeAfterImagesLoaded())
      .then(resolve)
  })
}

function renderChartFrame () {
  return new Promise((resolve, reject) => {
    this.$el.html(ChartTpl())
    this.styleBackground()
    this.handleInstructionsView()
    resolve(Promise.all([
      this.renderTotalResults(),
      this.renderVotingInstructions(),
      this.renderHeader()
    ]))
  })
}

function renderVisualization () {
  return new Promise((resolve, reject) => {
    if (this.views.has('visualization')) {
      this.views.get('visualization').remove()
      this.views.delete('visualization')
    }

    getVisualizationClass.call(this).then((imports) => {
      const Visualization = imports.default()
      const chart = new Visualization({
        app: this.app,
        model: this.model,
        settings: this.settings,
        dimensions: this.dimensions
      })

      this.views.set('visualization', chart)
      this.listenTo(chart, 'render', () => resolve(chart))
      this.$('.chart-main').append(chart.$el)
      chart.render()
    })
  })
}

function getVisualizationClass () {
  return new Promise((resolve, reject) => {
    if (SUPPORTED_ACTIVITIES.includes(this.model.type())) {
      const type = this.model.type() === 'ranking_poll' ? 'multiple_choice_poll' : this.model.type()
      // Without doing the ${...} below, lazy load fails in storybook because it's an expression.
      // './visualizations/' + type breaks karma.
      resolve(import(
        /* webpackChunkName: 'viz-chart-visualization-[request]' */
        `./visualizations/${type}`
      ))
    } else {
      reject(new ActivityNotSupportedError(`${this.model.type()} is not a valid activity`))
    }
  })
}

function renderTitle () {
  this.$el.html(ChartTpl())
  this.$el.toggleClass('chart--full-title', true)

  if (this.flashOptions.get('showInstructions')) {
    // TODO: Provide a better view state management mechanism
    // Proxy attributes for the title view
    this.flashOptions.proxy({
      voting_instructions_background_color: null,
      voting_instructions_text_color: null
    })

    this.renderVotingInstructions().then((view) => {
      if (!view) return
      this.$('.chart-header__instructions').append(view.el)
    })
  }

  return new Promise((resolve, reject) => {
    import(
      /* webpackChunkName: 'viz-title' */
      '@polleverywhere/viz-title'
    ).then((view) => {
      // TODO: Provide a better view state management mechanism
      // Proxy attributes for the title view
      this.model.proxy({
        sms_enabled: false,
        web_enabled: true,
        state: 'opened',
        current: true
      })

      const title = new view.Title({
        autoScale: false,
        model: this.model
      })
      this.listenTo(title, 'render', () => resolve(this))
      title.render()
      this.$('.chart-header--title').append(title.$el)
    }, reject)
  })
}

// Chart
//
// @usage
//   model = new MultipleChoice
//
//   view = new Component.Chart(model: model)
//   $("body").append view.render().$el
//
export class Chart extends Backbone.View {
  get className () {
    return 'chart component-chart'
  }

  constructor (...args) {
    super(...args)
    this.views = new Map()
  }

  initialize (options = {}) {
    this.options = _.defaults(options, {
      autoScale: true,
      width: null,
      height: null,
      i18n: undefined,
      showVotingInstructions: undefined,
      modelOverrides: {}
    })

    if (!this.options.i18n) {
      console.error('i18n is required, defaulting to English.')
    }
    this.model = proxyActivity(this.model)
    this.model.proxy(this.options.modelOverrides)
    this.user = options.user || new User()
    this.settings = options.settings || new Settings()
    this.app = options.app || new Backbone.Router()
    this.dimensions = options.dimensions || new Dimensions(null, {
      model: this.model,
      $el: this.$el,
      width: this.options.width,
      height: this.options.height
    })
    this.flashOptions = this.model.flashOptions()
    this.debouncedResizer = _.debounce(() => this.resize(), 50)

    Backbone.$(window).on('resize', this.debouncedResizer)
    this.on('font-loaded', this.resize)
    this.listenTo(this.flashOptions, 'change:background_color', this.setBGColor)
    this.listenTo(this.flashOptions, 'change:bgImageUrl', this.renderBackgroundImage)
    this.listenTo(this.flashOptions, 'change:bgImageOpacity', this.setBGImageOpacity)
    this.listenTo(this.flashOptions, 'change:bgImagePlacement', this.setBGImageSize)
    this.listenTo(this.model, 'change:display_state', () => {
      if (determineToReRender.call(this)) {
        this.removeSubviews()
        this.render()
      }

      this.handleInstructionsView()
    })

    this.$el.attr('owner', this.model.get('owner_login'))
    this.$el.attr('data-owner', this.model.get('owner_login'))
  }

  handleInstructionsView () {
    switch (this.model.type()) {
      case 'free_text_poll':
        if (this.model.get('display_state') === 'instructions') {
          this.flashOptions.proxy('background_color', colorToRgba(tokenColor('pe-neutral-050')))
          this.flashOptions.proxy('chart_primary_text_color', colorToRgba(tokenColor('pe-neutral-900')))
        } else {
          this.flashOptions.deproxy('background_color')
          this.flashOptions.deproxy('chart_primary_text_color')
        }
        break
      default:
    }
  }

  render () {
    if (SUPPORTED_ACTIVITIES.includes(this.model.type())) {
      renderChartOrTitle.call(this)
        .then(() => this.trigger('render'))
    } else {
      throw new ActivityNotSupportedError(`${this.model.type()} is not a valid activity`)
    }

    return this
  }

  resize () {
    this.scaleBody()
    this.dimensions.update()
  }

  scaleBody () {
    if (!this.options.autoScale) { return }
    this.$el.css({
      fontSize: `${getScale(
        this.dimensions.width(),
        this.dimensions.height()
      ) * 100}%`
    })
  }

  removeSubviews () {
    this.views.forEach((view) => view.remove())
    this.views.clear()

    return this
  }

  remove () {
    Backbone.$(window).off('resize', this.debouncedResizer)

    this.removeSubviews()
    super.remove(...arguments)
  }

  // Background image
  renderBackgroundImage () {
    let bgImageUrl = this.flashOptions.get('bgImageUrl')

    this.$('.background_image').empty()

    if (_.isEmpty(bgImageUrl)) {
      this.$('.background_image').css('background', '')
    } else {
      // If the image comes from our proxy then use it directly.
      // Otherwise patch it through the proxy for SSL to work
      const bgImageUri = URI(bgImageUrl)

      const opts = {}

      // reasonable default sizes
      opts.w = screen.width || 2000
      opts.h = screen.height || 2000

      // only downscale the image. don't upscale it
      opts.fit = 'max'

      // quality doesn't matter that much here because it's a background
      // image that will most likely be faded.
      opts.q = 30

      bgImageUrl = proxiedImageUrl(bgImageUri, opts)

      this.$('.background_image').css({
        backgroundPosition: 'center',
        backgroundImage: `url(${bgImageUrl})`
      })
      this.setBGImageSize()
    }
  }

  setBGImageOpacity () {
    const bgImageOpacity = this.flashOptions.get('bgImageOpacity')

    this.$('.background_image').css({
      opacity: bgImageOpacity,
      filter: `alpha(opacity=${bgImageOpacity * 100})`
    })
  }

  setBGImageSize () {
    let backgroundSize
    const bgImagePlacement = this.flashOptions.get('bgImagePlacement')
    switch (bgImagePlacement) {
      case 'stretched':
        backgroundSize = '100% 100%'
        break
      case 'centered':
        backgroundSize = 'cover'
        break
      case 'tiled':
        backgroundSize = 'auto'
        break
    }

    this.$('.background_image').css('backgroundSize', backgroundSize)
  }

  setBGColor () {
    this.$el.css('backgroundColor', this.flashOptions.get('background_color'))
  }

  styleBackground () {
    this.renderBackgroundImage()
    this.setBGImageSize()
    this.setBGImageOpacity()
    this.setBGColor()
  }

  renderHeader () {
    return new Promise((resolve, reject) => {
      if (this.views.has('header')) {
        resolve(this.views.get('header'))
      } else {
        const view = new ChartTitle({
          user: this.user,
          app: this.app,
          model: this.model,
          dimensions: this.dimensions,
          settings: this.settings
        })
        this.views.set('header', view)
        this.header = view

        view.on('resize', () => this.dimensions.update())
        view.render()
        this.$('.chart-header--title').append(view.$el)
        resolve(view)
      }
    })
  }

  renderVotingInstructions () {
    let shouldRenderVotingInstructions = this.flashOptions.get('showInstructions') ?? true

    if (typeof this.options.showVotingInstructions === 'boolean') {
      shouldRenderVotingInstructions = this.options.showVotingInstructions
    } else {
      switch (this.model.type()) {
        case 'competition':
          shouldRenderVotingInstructions = this.model.get('display_state') === 'welcome'
          break
        case 'free_text_poll':
          if (/instructions/i.test(this.model.get('display_state'))) {
            shouldRenderVotingInstructions = false
          }
          break
      }
    }

    return new Promise((resolve) => {
      if (!shouldRenderVotingInstructions) { return resolve() }

      if (this.views.has('instructions')) {
        resolve(this.views.get('instructions'))
      } else {
        const view = new VotingInstructions({
          i18n: this.options.i18n,
          model: this.model,
          settings: this.settings,
          user: this.user
        })

        this.views.set('instructions', view)
        this.listenTo(view, 'login qrcode resize', (...args) => this.trigger(...args))
        view.render()
        this.$('.chart-header__instructions').append(view.$el)
        resolve(view)
      }
    })
  }

  renderTotalResults () {
    return new Promise((resolve, reject) => {
      if (this.model.type() === 'survey') {
        return resolve()
      }

      if (this.views.has('results')) {
        resolve(this.views.get('results'))
      } else {
        const view = new ResultsContainer({
          model: this.model,
          settings: this.settings,
          dimensions: this.dimensions
        })
        this.views.set('results', view)
        view.render()

        this.$('.total_results_container').html(view.$el)
        resolve(view)
      }
    })
  }

  resizeAfterImagesLoaded () {
    const backgroundLoadedPromise = Backbone.$.Deferred()
    const imagesLoadedPromise = new Promise((resolve, reject) => {
      const imagePromise = this.$el.imagesLoaded()

      // If there are image tags (with a src) then trigger resize
      // after waiting 2 seconds if the images haven't loaded
      if (this.$('img[src]').length) {
        setTimeout(() => {
          if (imagePromise.state() === 'pending') {
            resolve()
          }
        }, 2000)
      }

      imagePromise.always(resolve)
    })

    this.$('.background_image').imagesLoaded({
      background: true
    }).always(() => backgroundLoadedPromise.resolve())

    return new Promise((resolve, reject) => {
      Promise.all([
        imagesLoadedPromise,
        backgroundLoadedPromise
      ]).finally(() => {
        this.dimensions.update()
        resolve()
      })
    })
  }
}
